(() => {
  const w = /** @type {any} */ (window);
  if (w.__mainCollectionSwatchesTooltipInitialized) return;
  w.__mainCollectionSwatchesTooltipInitialized = true;

  const TOOLTIP_ID = 'collection-swatch-tooltip';
  const OFFSET_PX = 10;
  const EDGE_PADDING = 8;

  /** @type {HTMLDivElement | null} */
  let tooltipEl = null;
  /** @type {HTMLElement | null} */
  let activeTarget = null;

  const ensureTooltip = () => {
    if (tooltipEl) return tooltipEl;

    const existing = document.getElementById(TOOLTIP_ID);
    if (existing instanceof HTMLDivElement) {
      tooltipEl = existing;
      return tooltipEl;
    }

    tooltipEl = document.createElement('div');
    tooltipEl.id = TOOLTIP_ID;
    tooltipEl.className = 'collection-swatch-tooltip';
    tooltipEl.setAttribute('role', 'tooltip');
    tooltipEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(tooltipEl);

    return tooltipEl;
  };

  const hide = () => {
    const tt = ensureTooltip();
    activeTarget?.removeAttribute('aria-describedby');
    activeTarget = null;

    tt.classList.remove('is-visible');
    tt.setAttribute('aria-hidden', 'true');
    tt.style.transform = 'translate3d(-9999px, -9999px, 0)';
  };

  /** @param {HTMLElement} target */
  const position = (target) => {
    const tt = ensureTooltip();

    const rect = target.getBoundingClientRect();
    const ttRect = tt.getBoundingClientRect();

    const idealLeft = rect.left + rect.width / 2 - ttRect.width / 2;
    const left = Math.min(Math.max(idealLeft, EDGE_PADDING), window.innerWidth - EDGE_PADDING - ttRect.width);

    const top = Math.max(EDGE_PADDING, rect.top - ttRect.height - OFFSET_PX);
    tt.style.transform = `translate3d(${Math.round(left)}px, ${Math.round(top)}px, 0)`;
  };

  /** @param {HTMLElement} target */
  const show = (target) => {
    const label = target.getAttribute('data-swatch-tooltip');
    if (!label) return;

    const tt = ensureTooltip();

    activeTarget?.removeAttribute('aria-describedby');
    activeTarget = target;

    tt.textContent = label;
    tt.setAttribute('aria-hidden', 'false');
    tt.classList.add('is-visible');

    target.setAttribute('aria-describedby', TOOLTIP_ID);

    requestAnimationFrame(() => position(target));
  };

  /** @param {EventTarget | null} maybeTarget */
  const asTooltipTarget = (maybeTarget) => {
    if (!(maybeTarget instanceof Element)) return null;
    const el = maybeTarget.closest('[data-swatch-tooltip]');
    return el instanceof HTMLElement ? el : null;
  };

  /** @param {HTMLElement} root */
  const bindRoot = (root) => {
    if (root.dataset.swatchTooltipBound === 'true') return;
    root.dataset.swatchTooltipBound = 'true';

    root.addEventListener(
      'pointerenter',
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerType === 'touch') return;
        const target = asTooltipTarget(event.target);
        if (!target) return;
        show(target);
      },
      true
    );

    root.addEventListener(
      'pointerleave',
      (event) => {
        const target = asTooltipTarget(event.target);
        if (!target) return;
        if (target === activeTarget) hide();
      },
      true
    );

    root.addEventListener(
      'focusin',
      (event) => {
        const target = asTooltipTarget(event.target);
        if (!target) return;
        show(target);
      },
      true
    );

    root.addEventListener(
      'focusout',
      (event) => {
        const target = asTooltipTarget(event.target);
        if (!target) return;
        if (target === activeTarget) hide();
      },
      true
    );

    root.addEventListener(
      'pointermove',
      (event) => {
        if (!(event instanceof PointerEvent) || event.pointerType === 'touch') return;
        if (!activeTarget) return;
        position(activeTarget);
      },
      { passive: true }
    );

    root.addEventListener('keydown', (event) => {
      if (event instanceof KeyboardEvent && event.key === 'Escape') hide();
    });
  };

  const initAll = () => {
    document.querySelectorAll('.main-collection[data-section-id]').forEach((node) => {
      if (node instanceof HTMLElement) bindRoot(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor: sections can be re-rendered dynamically.
  document.addEventListener('shopify:section:load', initAll);

  window.addEventListener('scroll', hide, { passive: true });
  window.addEventListener(
    'resize',
    () => {
      if (activeTarget) position(activeTarget);
    },
    { passive: true }
  );
})();
